import {
    useState,
    useEffect,
    useCallback,
    useRef,
    forwardRef,
    useImperativeHandle,
} from "react";
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    CircularProgress,
    useTheme,
    Paper,
} from "@mui/material";
import {
    CaseMinimalistic,
    Document,
    User,
    FolderOpen,
    Pen,
} from "@solar-icons/react/ssr";
import { request } from "@/Api";

/**
 * Entity type configuration — icon, color, and display label for each searchable type.
 */
const ENTITY_CONFIG = {
    projects: {
        icon: CaseMinimalistic,
        color: "#A9E8CB",
        label: "Projects",
        mentionType: "project",
    },
    bids: {
        icon: Document,
        color: "#FFD597",
        label: "Bids",
        mentionType: "bid",
    },
    persons: {
        icon: User,
        color: "#80C8FF",
        label: "People",
        mentionType: "person",
    },
    clients: {
        icon: FolderOpen,
        color: "#80C8FF",
        label: "Clients",
        mentionType: "client",
    },
    canvases: {
        icon: Pen,
        color: "#C9A9FF",
        label: "Canvases",
        mentionType: "canvas",
    },
};

/**
 * Flattens grouped search results into a single ordered list for keyboard navigation.
 * Each item includes its group key and entity type for mention creation.
 */
function flattenResults(results) {
    const items = [];
    for (const groupKey of Object.keys(ENTITY_CONFIG)) {
        const entities = results[groupKey] || [];
        for (const entity of entities) {
            items.push({
                ...entity,
                groupKey,
                mentionType: ENTITY_CONFIG[groupKey].mentionType,
            });
        }
    }
    return items;
}

/**
 * MentionDropdown — floating dropdown triggered by @ in the chat input.
 *
 * Always mounted in the JSX tree (hooks are stable). Renders nothing when
 * query is null; shows the dropdown when query is a string.
 *
 * Exposes handleKeyDown via ref so the parent can delegate keyboard events.
 *
 * Props:
 *   query       – the search text after @ (null = hidden, "" = show all)
 *   onSelect    – ({ type, id, label }) => void
 *   onDismiss   – () => void
 *
 * Ref API:
 *   ref.current.handleKeyDown(event) — call from the parent's onKeyDown
 */
const MentionDropdown = forwardRef(function MentionDropdown(
    { query, onSelect, onDismiss },
    ref,
) {
    const theme = useTheme();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const listRef = useRef(null);

    const isOpen = query !== null;

    /**
     * Debounced search — fires 250ms after user stops typing after @.
     */
    useEffect(() => {
        if (!isOpen) {
            setResults(null);
            return;
        }

        setLoading(true);
        const timeout = setTimeout(() => {
            request
                .get("/search", { params: { search_string: query } })
                .then((response) => {
                    setResults(response.data);
                    setActiveIndex(0);
                })
                .catch(() => setResults(null))
                .finally(() => setLoading(false));
        }, 250);

        return () => clearTimeout(timeout);
    }, [query, isOpen]);

    const flatItems = results ? flattenResults(results) : [];

    /**
     * Keyboard navigation handler — exposed via useImperativeHandle
     * so the parent can call ref.current.handleKeyDown(event).
     */
    const handleKeyDown = useCallback(
        (event) => {
            if (!isOpen) return;
            if (flatItems.length === 0) {
                if (event.key === "Escape") {
                    event.preventDefault();
                    onDismiss();
                }
                return;
            }

            if (event.key === "ArrowDown") {
                event.preventDefault();
                setActiveIndex((prev) =>
                    Math.min(prev + 1, flatItems.length - 1),
                );
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActiveIndex((prev) => Math.max(prev - 1, 0));
            } else if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault();
                const selected = flatItems[activeIndex];
                if (selected) {
                    onSelect({
                        type: selected.mentionType,
                        id: selected.id,
                        label: selected.name,
                    });
                }
            } else if (event.key === "Escape") {
                event.preventDefault();
                onDismiss();
            }
        },
        [isOpen, flatItems, activeIndex, onSelect, onDismiss],
    );

    useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

    // Scroll active item into view
    useEffect(() => {
        if (listRef.current) {
            const activeElement = listRef.current.querySelector(
                `[data-index="${activeIndex}"]`,
            );
            if (activeElement) {
                activeElement.scrollIntoView({ block: "nearest" });
            }
        }
    }, [activeIndex]);

    // Don't render anything when the dropdown is closed
    if (!isOpen) return null;

    const hasResults = flatItems.length > 0;
    const showEmpty = results && !loading && !hasResults;

    return (
        <Paper
            elevation={6}
            sx={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                right: 0,
                mb: 0.5,
                maxHeight: 320,
                overflowY: "auto",
                borderRadius: 2.5,
                zIndex: 10,
            }}
            ref={listRef}
        >
            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {showEmpty && (
                <Box sx={{ py: 2, px: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        No results for "{query}"
                    </Typography>
                </Box>
            )}

            {hasResults && (
                <List dense disablePadding>
                    {Object.entries(ENTITY_CONFIG).map(([groupKey, config]) => {
                        const groupEntities = results[groupKey] || [];
                        if (groupEntities.length === 0) return null;

                        const Icon = config.icon;

                        return (
                            <Box key={groupKey}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        px: 2,
                                        pt: 1.5,
                                        pb: 0.5,
                                        display: "block",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        fontSize: 11,
                                        letterSpacing: "0.05em",
                                    }}
                                >
                                    {config.label}
                                </Typography>
                                {groupEntities.map((entity) => {
                                    const globalIndex = flatItems.findIndex(
                                        (item) =>
                                            item.id === entity.id &&
                                            item.groupKey === groupKey,
                                    );
                                    const isActive =
                                        globalIndex === activeIndex;

                                    return (
                                        <ListItem
                                            key={`${groupKey}-${entity.id}`}
                                            data-index={globalIndex}
                                            onClick={() =>
                                                onSelect({
                                                    type: config.mentionType,
                                                    id: entity.id,
                                                    label: entity.name,
                                                })
                                            }
                                            sx={{
                                                cursor: "pointer",
                                                backgroundColor: isActive
                                                    ? "rgba(255, 255, 255, 0.12)"
                                                    : "transparent",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "rgba(255, 255, 255, 0.08)",
                                                },
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 1,
                                                mx: 0.5,
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Box
                                                    sx={{
                                                        backgroundColor:
                                                            config.color,
                                                        borderRadius: 1,
                                                        p: 0.5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                    }}
                                                >
                                                    <Icon
                                                        size={16}
                                                        color="#232323"
                                                    />
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={entity.name}
                                                secondary={
                                                    [entity.data1, entity.data2]
                                                        .filter(Boolean)
                                                        .join(" · ") ||
                                                    undefined
                                                }
                                                primaryTypographyProps={{
                                                    variant: "body2",
                                                    noWrap: true,
                                                    fontWeight: isActive
                                                        ? 600
                                                        : 400,
                                                }}
                                                secondaryTypographyProps={{
                                                    variant: "caption",
                                                    noWrap: true,
                                                }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </List>
            )}
        </Paper>
    );
});

export default MentionDropdown;
